// ConnectRepoProvider / useConnectRepo (RFC-0002 Task 3.4).
//
// Shared open/close state so all three "Connect repository" controls drive
// exactly ONE modal instance. Mirrors the shape of context/theme.tsx.
//
// Note: exporting both the component (provider) and the hook from one file
// triggers the same accepted `react-refresh/only-export-components` lint
// *warning* as theme.tsx — lint still passes; this mirrors the repo pattern.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface ConnectRepoContextValue {
  /** Whether the connect modal is currently open. */
  isOpen: boolean;
  /** Open the connect modal. */
  open: () => void;
  /** Close the connect modal. */
  close: () => void;
}

const ConnectRepoContext = createContext<ConnectRepoContextValue | null>(null);

export function ConnectRepoProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<ConnectRepoContextValue>(
    () => ({ isOpen, open, close }),
    [isOpen, open, close],
  );

  return <ConnectRepoContext.Provider value={value}>{children}</ConnectRepoContext.Provider>;
}

/**
 * Access the connect-repo modal controls. Returns safe no-op defaults when
 * called outside a provider (never throws), matching theme.tsx's posture.
 */
export function useConnectRepo(): ConnectRepoContextValue {
  const ctx = useContext(ConnectRepoContext);
  if (ctx) return ctx;
  return { isOpen: false, open: () => {}, close: () => {} };
}
